import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ForgotPassword from './ForgotPassword';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { connect } from 'react-redux';
import {
  refreshToken,
  signInUser,
  OtpErrorType,
} from '../../pages/Auth/actions';
import { AuthData, SignInErrorType } from '../../pages/Auth/reducer';
import { CircularProgress, useTheme } from '@mui/material';
import './styles.css';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GlassContainer,
  GlassCard,
  GlassTextField,
  GlassButton,
  SectionHeader,
  GlassColors,
  ButtonTertiaryIcon,
} from '../glassmorphism/GlassmorphismStyles';
import { ReactComponent as CloseIcon } from '../../assets/icons/nav/close.svg';
import { trackEvent } from '../../analytics';
import SignInSkeleton from '../skeletons/SignInSkeleton';

interface DispatchFromProps {
  signInUser: (username: string, password: string) => void;
  refreshToken: (refreshToken: string) => void;
}

interface StateFromProps {
  isError: boolean;
  refreshTokenTimeout?: any;
  authData?: AuthData;
  isLoading: boolean;
  errorType?: SignInErrorType | OtpErrorType;
}

interface PassedProps {
  disableCustomTheme?: boolean;
}

const SignIn = (props: PassedProps & DispatchFromProps & StateFromProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Local states for errors
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [fieldChangedAfterLoginAttempt, setFieldChangedAfterLoginAttempt] =
    React.useState(false);
  const [showSkeleton, setShowSkeleton] = React.useState(true);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 200);

    return () => clearTimeout(timer);
  }, []);
  // ---- NEW: State for "Remember Me" and Email field
  const [rememberMe, setRememberMe] = React.useState(false);
  const [savedEmail, setSavedEmail] = React.useState(''); // The email to display in the input

  const location = useLocation();
  // If user is already authenticated, navigate to profile, if user authenticated
  //  from /browse-handyman navigate to browse-handyman
  React.useEffect(() => {
    if (props.authData) {
      if (location.state?.from === 'browse-handyman') {
        navigate('/browse-handyman', { replace: true });
      } else if (location.state?.from === '/verification') {
        navigate('/verification', { replace: true });
      } else {
        navigate('/profile', { replace: true });
      }
    }
  }, [props.authData, navigate, location.state]);

  // ---- NEW: On mount, prefill saved email from localStorage if it exists
  React.useEffect(() => {
    const storedEmail = localStorage.getItem('savedEmail');
    if (storedEmail) {
      setSavedEmail(storedEmail);
      setRememberMe(true); // Mark checkbox as checked if we have a stored email
    }
  }, []);

  // Reset fieldChangedAfterLoginAttempt when error occurs to show error message
  React.useEffect(() => {
    if (props.errorType === SignInErrorType.incorrectCredentials) {
      setFieldChangedAfterLoginAttempt(false);
    }
  }, [props.errorType]);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // Called by the form "onSubmit" event
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // If inputs are invalid, do not proceed
    if (emailError || passwordError) {
      return;
    }

    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    const password = data.get('password') as string;

    setFieldChangedAfterLoginAttempt(true);

    // ---- NEW: If rememberMe is checked, store email in localStorage.
    //           Otherwise, remove it.
    if (rememberMe) {
      localStorage.setItem('savedEmail', email);
    } else {
      localStorage.removeItem('savedEmail');
    }

    trackEvent('sign_in');
    props.signInUser(email, password);
  };

  // Basic validations
  const validateInputs = () => {
    const emailInput = document.getElementById('email') as HTMLInputElement;
    const passwordInput = document.getElementById(
      'password',
    ) as HTMLInputElement;

    let isValid = true;

    if (!emailInput.value || !/\S+@\S+\.\S+/.test(emailInput.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else if (
      props.errorType === SignInErrorType.incorrectCredentials &&
      !fieldChangedAfterLoginAttempt
    ) {
      setEmailError(true);
      setEmailErrorMessage('Password or email is incorrect.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!passwordInput.value || passwordInput.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else if (
      props.errorType === SignInErrorType.incorrectCredentials &&
      !fieldChangedAfterLoginAttempt
    ) {
      setPasswordError(true);
      setPasswordErrorMessage('Password or email is incorrect.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  if ((props.isLoading || showSkeleton) && !props.authData) {
    return <SignInSkeleton isDark />;
  }

  return (
    <GlassContainer>
      <Box
        width={{ xs: '90%', sm: '450px' }}
        mx='auto'
        mt={{ xs: 12.5, md: 15 }}
        pb={4}
      >
        <GlassCard
          sx={{
            p: { xs: '24px 24px 32px 24px', sm: '32px 32px 48px 32px' },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            position: 'relative',
          }}
        >
          <ButtonTertiaryIcon
            onClick={() => navigate('/')}
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: { xs: '16px', sm: '32px' },
              right: { xs: '16px', sm: '32px' },
            }}
          >
            <CloseIcon />
          </ButtonTertiaryIcon>
          <SectionHeader
            sx={{
              textAlign: 'center',
              fontSize: { xs: '20px', sm: '24px' },
              filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
              mb: 0,
              color: isDark
                ? GlassColors.dark.accent
                : GlassColors.light.secondary,
            }}
          >
            Sign in
          </SectionHeader>
          <Box
            component='form'
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: { xs: 1, sm: 2 },
            }}
          >
            <FormControl>
              <FormLabel
                htmlFor='email'
                sx={{
                  fontFamily: 'roboto, sans-serif',
                  color: isDark
                    ? GlassColors.dark.textPrimary
                    : GlassColors.light.textPrimary,
                }}
              >
                Email*
              </FormLabel>
              <GlassTextField
                error={emailError}
                helperText={emailErrorMessage}
                id='email'
                type='email'
                name='email'
                value={savedEmail}
                onChange={(e) => {
                  setSavedEmail(e.target.value);
                  setFieldChangedAfterLoginAttempt(true);
                }}
                placeholder=''
                autoComplete='email'
                autoFocus
                required
                fullWidth
                color={emailError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormLabel
                htmlFor='password'
                sx={{
                  fontFamily: 'roboto, sans-serif',
                  color: isDark
                    ? GlassColors.dark.textPrimary
                    : GlassColors.light.textPrimary,
                }}
              >
                Password*
              </FormLabel>
              <GlassTextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name='password'
                placeholder=''
                type='text'
                id='password'
                autoComplete='current-password'
                autoFocus
                required
                fullWidth
                color={passwordError ? 'error' : 'primary'}
                onChange={() => setFieldChangedAfterLoginAttempt(true)}
              />
            </FormControl>

            {/** ---- NEW: We control the checkbox via "rememberMe" state */}
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label='Remember me'
              sx={{
                '& .MuiTypography-root': {
                  fontFamily: 'roboto, sans-serif',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 700,
                  lineHeight: { xs: '157%', sm: '175%' },
                  letterSpacing: '.15px',
                  color: isDark
                    ? GlassColors.dark.textPrimary
                    : GlassColors.light.textPrimary,
                },
              }}
            />

            <Box sx={{ textAlign: 'center', mt: { xs: '16px', sm: 0 } }}>
              <ForgotPassword open={open} handleClose={handleClose} />
              <GlassButton
                type='submit'
                fullWidth
                onClick={validateInputs}
                disabled={props.isLoading}
                sx={{
                  position: 'relative',
                  mb: { xs: '8px', sm: '16px' },
                  fontSize: { xs: '12px', sm: '16px' },
                  lineHeight: 2,
                  fontWeight: 500,
                }}
              >
                {props.isLoading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'white',
                    }}
                  />
                ) : (
                  'Sign in'
                )}
              </GlassButton>
              <span className='error-message'>
                {props.errorType === SignInErrorType.incorrectCredentials
                  ? 'Password or email is incorrect'
                  : ''}
              </span>
              <Link
                component='button'
                type='button'
                onClick={handleClickOpen}
                variant='body2'
                sx={{
                  fontFamily: 'roboto, sans-serif',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 700,
                  lineHeight: { xs: '157%', sm: '175%' },
                  letterSpacing: { xs: '.1px', sm: '.15px' },
                  color: isDark
                    ? GlassColors.dark.textPrimary
                    : GlassColors.light.textPrimary,
                }}
              >
                Forgot your password?
              </Link>
            </Box>
          </Box>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: { sx: '14px', sm: '16px' },
              fontFamily: 'roboto, sans-serif',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
            }}
          >
            Don&apos;t have an account?{' '}
            <Link
              href='sign-up'
              variant='body2'
              sx={{
                alignSelf: 'center',
                fontWeight: 700,
                fontSize: { sx: '14px', sm: '16px' },
              }}
            >
              Sign up
            </Link>
          </Typography>
        </GlassCard>
      </Box>
    </GlassContainer>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    isError: state.user.isError,
    refreshTokenTimeout: state.user.refreshTokenTimeout,
    authData: state.user.authData,
    isLoading: state.user.isLoading,
    errorType: state.user.errorType,
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  signInUser: signInUser,
  refreshToken: refreshToken,
})(SignIn);
